import { useEffect, useState } from "react";
import {
  KARTY,
  KARTY_PHASE_LABEL,
  type KartyPublicPlayer,
  type KartySubmission,
} from "@ziolo/shared";
import { Card } from "@ziolo/ui";
import { useSession } from "../session/SessionProvider.js";
import { useKartySocket } from "../games/karty/useKartySocket.js";
import "../styles/karty.css";

function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (deadline === null) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);
  return left;
}

export function KartyPage() {
  const { user, restoring } = useSession();
  const enabled = Boolean(user) && !restoring;
  const { table, session, connected, error, notice, playCard, chooseWinner } = useKartySocket(enabled);
  const nextRoundLeft = useCountdown(table.phase === "result" ? table.nextRoundAt : null);

  if (restoring) {
    return <p className="hub-hint">Ładowanie…</p>;
  }

  if (!user) {
    return (
      <Card title="Karty" icon="🃎">
        <p className="hub-hint">Zaloguj się w belce na górze, żeby dołączyć do stołu.</p>
      </Card>
    );
  }

  const played = session?.playedCard !== null && session?.playedCard !== undefined;
  const canPlay =
    connected &&
    table.phase === "playing" &&
    session !== null &&
    !session.isCzar &&
    !played;
  const canJudge = connected && table.phase === "judging" && Boolean(session?.isCzar);

  return (
    <div className="karty">
      <header className="karty__header">
        <div>
          <h2 className="karty__title">
            <span className="z-icon" aria-hidden="true">
              🃎
            </span>
            Karty Przeciwko Ludzkości
          </h2>
          <p className="karty__welcome">Witaj, {user.username}!</p>
        </div>
        <p className="karty__status" data-testid="karty-status">
          Graczy: {table.playerCount}
          {connected ? "" : " · łączenie…"}
        </p>
      </header>

      {error ? <p className="hub-error">{error}</p> : null}
      {notice ? <p className="karty__notice">{notice}</p> : null}

      <div className="karty__layout">
        <div className="karty__stage">
          <section className="karty-area" aria-label="Czarna karta">
            {table.phase === "waiting" ? (
              <p className="karty__wait">
                {table.needed > 0
                  ? `Oczekiwanie na graczy… jeszcze ${table.needed} (minimum ${KARTY.MIN_PLAYERS}).`
                  : "Oczekiwanie na graczy…"}
              </p>
            ) : table.phase === "result" && table.result ? (
              <article className="karty-card karty-card--black">
                🏆 {table.result.winnerName} wygrał za: &quot;{table.result.card}&quot;
              </article>
            ) : table.prompt ? (
              <article className="karty-card karty-card--black">{table.prompt}</article>
            ) : null}
            {table.phase === "result" && nextRoundLeft > 0 ? (
              <p className="karty__hint">Następna runda za {nextRoundLeft} s</p>
            ) : null}
          </section>

          <section className="karty-area" aria-label="Twoja ręka">
            {session?.isCzar && table.phase === "playing" ? (
              <p className="karty__hint">Jesteś Czar Rundy. Czekaj na karty innych.</p>
            ) : session && session.hand.length > 0 && table.phase !== "waiting" ? (
              session.hand.map((card, index) => {
                const selected = session.playedCard === card;
                return (
                  <button
                    key={`${card}-${index}`}
                    type="button"
                    className={`karty-card karty-card--white${selected ? " karty-card--played" : ""}${
                      !canPlay ? " karty-card--disabled" : ""
                    }`}
                    disabled={!canPlay}
                    onClick={() => playCard(card)}
                  >
                    {card}
                  </button>
                );
              })
            ) : table.phase === "playing" ? (
              <p className="karty__hint">Czekaj na karty.</p>
            ) : null}
          </section>

          <section className="karty-area" aria-label="Zagrane karty">
            {canJudge ? (
              <>
                <h3 className="karty__subtitle">Wybierz zwycięzcę:</h3>
                {session?.submissions.map((row: KartySubmission) => (
                  <button
                    key={row.userId}
                    type="button"
                    className="karty-card karty-card--white"
                    onClick={() => chooseWinner(row.userId)}
                  >
                    {row.name}: {row.card}
                  </button>
                ))}
              </>
            ) : table.phase === "judging" ? (
              <p className="karty__hint">Czar rundy wybiera zwycięzcę…</p>
            ) : null}
          </section>
        </div>

        <aside className="karty__side">
          <Card title="Wyniki" icon="🏆" variant="panel">
            {table.players.length === 0 ? (
              <p className="hub-hint">Nikogo jeszcze nie ma.</p>
            ) : (
              <ul className="karty-scores">
                {table.players.map((player: KartyPublicPlayer) => (
                  <li key={player.userId}>
                    <span>
                      {player.name}
                      {player.isCzar ? " · Czar" : ""}
                      {player.played && !player.isCzar ? " · zagrał" : ""}
                    </span>
                    <strong>{player.score}</strong>
                  </li>
                ))}
              </ul>
            )}
            <p className="karty__phase">{KARTY_PHASE_LABEL[table.phase]}</p>
          </Card>
        </aside>
      </div>
    </div>
  );
}
